/**
 * AddressFormModal - Universal Address Modal Component
 * Handles creation and editing of addresses for merchants, owners, and other entities
 * 
 * Features:
 * - Address autocomplete with Google Places API
 * - Real-time validation
 * - Support for multiple address types (PHYSICAL, MAILING, BILLING)
 * - Can work with or without API (controlled/uncontrolled mode)
 * - Edit mode support
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import AddressAutocomplete from 'components/AddressAutocomplete';
import NotificationBanner from 'components/NotificationBanner';
import { 
  validateRequiredString, 
  validateStreetAddress2,
  validateZipCode, 
  formatZipCode,
  validateAddressPhoneNumber,
  filterAddressPhoneNumber
} from 'modules/InputsValidations';

const AddressFormModal = ({ 
  isOpen = false,
  toggle = () => {},
  title = 'Add Address',
  onSave = () => {},
  addressData = null, // For editing
  availableAddressTypes = ['PHYSICAL', 'MAILING', 'BILLING'],
  showPhoneNumber = false,
  loading = false,
  // API mode props (optional)
  apiEndpoint = null, // If provided, will save directly to API
  apiService = null,
}) => {
  
  // Form state
  const [formData, setFormData] = useState({
    addressType: 'PHYSICAL',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    phoneNumber: ''
  });
  
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });
  const [zipEditable, setZipEditable] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with editing data or reset
  useEffect(() => {
    if (isOpen) {
      if (addressData) {
        // Edit mode
        setFormData({
          addressType: addressData.addressType || 'PHYSICAL',
          street1: addressData.street1 || addressData.streetAddress || '',
          street2: addressData.street2 || addressData.streetAddress2 || '',
          city: addressData.city || '',
          state: addressData.state || '',
          zipCode: addressData.zipCode || '',
          country: addressData.country || '',
          phoneNumber: addressData.phoneNumber || ''
        });
        setZipEditable(true);
      } else {
        // Create mode
        const defaultType = availableAddressTypes.length > 0 ? availableAddressTypes[0] : 'PHYSICAL';
        setFormData({
          addressType: defaultType,
          street1: '',
          street2: '',
          city: '',
          state: '',
          zipCode: '',
          country: '',
          phoneNumber: ''
        });
        setZipEditable(false);
      }
      setErrors({});
      setNotification({ show: false, type: 'success', message: '' });
    }
  }, [isOpen, addressData, availableAddressTypes]);

  // Notification helpers
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: 'success', message: '' });
    }, 5000);
  };

  const closeNotification = () => {
    setNotification({ show: false, type: 'success', message: '' });
  };

  // Handle input changes with validation
  const handleInputChange = (field, value) => {
    let processedValue = value;
    
    // Apply formatting
    if (field === 'zipCode') {
      processedValue = formatZipCode(value);
    } else if (field === 'phoneNumber') {
      processedValue = filterAddressPhoneNumber(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));
    
    // Real-time validation
    validateField(field, processedValue);
  };

  // Validate individual field
  const validateField = (field, value) => {
    let validationResult = { isValid: true, message: '' };
    
    switch (field) {
      case 'addressType':
        validationResult = validateRequiredString(value, 255, 'Address Type');
        break;
      case 'street1':
        validationResult = validateRequiredString(value, 255, 'Street Address');
        break;
      case 'street2':
        validationResult = validateStreetAddress2(value);
        break;
      case 'zipCode':
        if (value && value.trim() !== '') {
          validationResult = validateZipCode(value);
        }
        break;
      case 'city':
        validationResult = validateRequiredString(value, 255, 'City');
        break;
      case 'phoneNumber':
        if (showPhoneNumber && value && value.trim() !== '') {
          validationResult = validateAddressPhoneNumber(value, 'Phone Number');
        }
        break;
      default:
        break;
    }
    
    if (!validationResult.isValid) {
      setErrors(prev => ({
        ...prev,
        [field]: validationResult.message
      }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle address autocomplete change
  const handleAddressChange = (text) => {
    setFormData(prev => ({
      ...prev,
      street1: text,
      city: '',
      state: '',
      zipCode: '',
      country: ''
    }));
    setZipEditable(false);
    setErrors(prev => {
      const copy = { ...prev };
      delete copy.city;
      delete copy.zipCode;
      delete copy.street1;
      delete copy.state;
      delete copy.country;
      return copy;
    });
  };

  // Handle address autocomplete selection
  const handleAddressSelect = (item) => {
    const label = item?.label || '';
    const details = item?.details || {};
    const city = details.city || '';
    const state = details.state || '';
    const country = details.country || '';
    const zipCode = details.zipCode || details.postalCode || '';
    
    setFormData(prev => ({
      ...prev,
      street1: label,
      city,
      state,
      country,
      zipCode
    }));
    
    setZipEditable(!zipCode);
    
    // Validate autocompleted fields
    const nextErrors = { ...errors };
    if (!label.trim()) {
      nextErrors.street1 = 'Street Address is required';
    } else {
      delete nextErrors.street1;
    }
    if (!city.trim()) {
      nextErrors.city = 'City is required';
    } else {
      delete nextErrors.city;
    }
    if (zipCode && !validateZipCode(zipCode).isValid) {
      nextErrors.zipCode = validateZipCode(zipCode).message;
    } else {
      delete nextErrors.zipCode;
    }
    setErrors(nextErrors);
  };

  // Check if form is complete
  const isFormComplete = () => {
    const requiredFields = ['addressType', 'street1', 'city'];
    const allFilled = requiredFields.every(field => {
      const value = formData[field];
      return value && value.trim() !== '';
    });
    return allFilled && Object.keys(errors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    // Final validation
    if (!isFormComplete()) {
      const newErrors = {};
      if (!formData.addressType || formData.addressType.trim() === '') {
        newErrors.addressType = 'Address Type is required';
      }
      if (!formData.street1 || formData.street1.trim() === '') {
        newErrors.street1 = 'Street Address is required';
      }
      if (!formData.city || formData.city.trim() === '') {
        newErrors.city = 'City is required';
      }
      setErrors(newErrors);
      showNotification('error', 'Please fill in all required fields');
      return;
    }

    // If API mode is enabled
    if (apiEndpoint && apiService) {
      setIsSubmitting(true);
      try {
        const result = await apiService.post(apiEndpoint, formData);
        showNotification('success', 'Address saved successfully!');
        onSave(result, addressData);
        toggle();
      } catch (error) {
        console.error('Error saving address:', error);
        showNotification('error', `Error saving address: ${error.message}`);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Callback mode
      onSave(formData, addressData);
      toggle();
    }
  };

  const isEditMode = !!addressData;
  const isBusy = loading || isSubmitting;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        {title}
      </ModalHeader>
      <ModalBody>
        {/* Notification Banner */}
        <NotificationBanner
          type={notification.type}
          message={notification.message}
          show={notification.show}
          onClose={closeNotification}
        />

        <Row>
          <Col md={showPhoneNumber ? "6" : "12"}>
            <FormGroup>
              <Label for="addressType">Address Type <span>*</span></Label>
              <Input
                id="addressType"
                type="select"
                value={formData.addressType}
                onChange={(e) => handleInputChange('addressType', e.target.value)}
                className={errors.addressType ? 'is-invalid' : ''}
                bsSize="sm"
              >
                <option value="">Select Type</option>
                {availableAddressTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </Input>
              {errors.addressType && (
                <div className="invalid-feedback d-block">{errors.addressType}</div>
              )}
            </FormGroup>
          </Col>
          {showPhoneNumber && (
            <Col md="6">
              <FormGroup>
                <Label for="phoneNumber">Phone Number <span>*</span></Label>
                <Input
                  id="phoneNumber"
                  type="text"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  placeholder="Enter phone number"
                  className={errors.phoneNumber ? 'is-invalid' : ''}
                  maxLength={25}
                  bsSize="sm"
                />
                {errors.phoneNumber && (
                  <div className="invalid-feedback d-block">{errors.phoneNumber}</div>
                )}
              </FormGroup>
            </Col>
          )}
        </Row>

        <Row>
          <Col md="12">
            <FormGroup>
              <Label for="street1">Street Address 1 <span>*</span></Label>
              <AddressAutocomplete
                value={formData.street1}
                onChange={handleAddressChange}
                onSelect={handleAddressSelect}
                placeholder="Start typing address..."
                className={errors.street1 ? 'is-invalid' : ''}
                bsSize="sm"
              />
              {errors.street1 && (
                <div className="invalid-feedback d-block">{errors.street1}</div>
              )}
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="12">
            <FormGroup>
              <Label for="street2">Street Address 2</Label>
              <Input
                id="street2"
                type="text"
                value={formData.street2}
                onChange={(e) => handleInputChange('street2', e.target.value)}
                placeholder="Apartment, suite, etc. (optional)"
                className={errors.street2 ? 'is-invalid' : ''}
                maxLength={255}
                bsSize="sm"
              />
              {errors.street2 && (
                <div className="invalid-feedback d-block">{errors.street2}</div>
              )}
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="city">City <span>*</span></Label>
              <Input
                id="city"
                type="text"
                value={formData.city}
                readOnly
                disabled
                placeholder="Enter city"
                className={`bg-light border-secondary ${errors.city ? 'is-invalid' : ''}`}
                bsSize="sm"
              />
              {errors.city && (
                <div className="invalid-feedback d-block">{errors.city}</div>
              )}
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for="state">State <span>*</span></Label>
              <Input
                id="state"
                type="text"
                value={formData.state}
                readOnly
                disabled
                placeholder="Enter state"
                className="bg-light border-secondary"
                bsSize="sm"
              />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="zipCode">Zip Code <span>*</span></Label>
              <Input
                id="zipCode"
                type="text"
                value={formData.zipCode}
                onChange={(e) => handleInputChange('zipCode', e.target.value)}
                placeholder="Enter zip code"
                readOnly={!zipEditable}
                disabled={!zipEditable}
                className={`${!zipEditable ? 'bg-light border-secondary ' : ''}${errors.zipCode ? 'is-invalid' : ''}`}
                bsSize="sm"
              />
              {errors.zipCode && (
                <div className="invalid-feedback d-block">{errors.zipCode}</div>
              )}
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for="country">Country <span>*</span></Label>
              <Input
                id="country"
                type="text"
                value={formData.country}
                readOnly
                disabled
                placeholder="Enter country"
                className="bg-light border-secondary"
                bsSize="sm"
              />
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>
      
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSave} disabled={!isFormComplete()}>
          {isEditMode ? 'Update' : 'Add'} Address
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddressFormModal;

